import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { calculatePercentage, formatDataGeneral, getAttributeValue, getFirstFileLinkIfExist } from "utils/utils";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import { renderDpatEvidenceLink, scoreOnlyIfRowEvidenceComplete } from "utils/renderDpatEvidenceLink";
import Comment from "components/Comments";

const EducationServiceSupport = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  const [schools, setSchools] = useState([]);
  const [educationSupport, setEducationSupport] = useState([]);
  const [score, setScore] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [maxScore, setMaxScore] = useState(1);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "PI4",
      area: "Access to Social Services",
      maxScore,
      educationSupport,
      score,
      percentage
    }),
  }));

  const DPAT_SLUG = "pi4.0-4.1";

  const schoolsColumns = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
    },
    {
      title: 'School Name',
      dataIndex: 'school',
      key: 'school',
    },
    {
      title: 'Community',
      dataIndex: 'community',
      key: 'community',
    },
    {
      title: 'Establishment Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Support(s)',
      dataIndex: 'support',
      key: 'support',
    },
    {
      title: 'Support Evidence',
      dataIndex: 'report',
      key: 'report',
      render: (value) => renderDpatEvidenceLink(value, "View Evidence"),
    },
  ];

  const schoolsSupportColumns = [
    {
      title: 'No of Schools',
      dataIndex: 'no',
      key: 'no',
    },
    {
      title: 'No of Schools Supported',
      dataIndex: 'shoolSupported',
      key: 'shoolSupported',
    },
    {
      title: 'Percentage of Schools Supported',
      dataIndex: 'percentage',
      key: 'Percentage of Public Schools Supported',
    }
  ]

  const loadFromDhis2 = useCallback(() => {
    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=g27TeeehRQC&startDate=${year}-01-01&endDate=${year}-12-31`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=g27TeeehRQC&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31`)
            .then(resp => {
              const schools = result.data.instances;
              const reports = resp.data.instances;
              const temp = [];

              const publicSchools = formatDataGeneral(schools, "Type", "Public") || [];

              publicSchools.forEach((school, idx) => {
                const currentReport = reports.find(rep => (rep.trackedEntity === school.trackedEntity)
                  && rep.programStage === "z6mWkIfypaw");
                const reportLink = getFirstFileLinkIfExist(reports, "E6mSis1p8NG", school.trackedEntity, "z6mWkIfypaw");
                let support = "";

                if (currentReport) {
                  currentReport.dataValues.forEach(rep => {
                    if (rep.dataElement === "xAySRtnwzQa") {
                      support = rep.value;
                    }
                  });
                }

                const tempDataSet = {
                  no: idx + 1,
                  school: getAttributeValue("Name of School", school),
                  community: getAttributeValue("Community", school),
                  date: getAttributeValue("Established Date", school),
                  support,
                  report: reportLink
                    ? `https://dddp.gov.gh/api/events/files?eventUid=${reportLink}&dataElementUid=E6mSis1p8NG`
                    : "Not Uploaded",
                };

                temp.push(tempDataSet);
              });

              setSchools(temp);

              const supportedRows = temp.filter((school) => school.support !== "");
              const schoolSupported = supportedRows.length;
              const percentageNum = Number(
                calculatePercentage(schoolSupported, temp.length).toFixed(2),
              );
              const rawScore = percentageNum >= 15 ? 1 : 0;

              setScore(
                scoreOnlyIfRowEvidenceComplete(supportedRows, ["report"], rawScore),
              );
              setPercentage(percentageNum);

              setEducationSupport([{
                no: temp.length,
                shoolSupported: schoolSupported,
                percentage: percentageNum.toFixed(2),
              }]);


            })
            .catch(err => console.log(err))
        }
      })
      .catch(err => console.log(err))
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.educationSupport) setEducationSupport(snap.educationSupport);
      if (snap.score != null) setScore(snap.score);
      if (snap.percentage != null) setPercentage(snap.percentage);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  return (
    <Comment
      data={educationSupport}
      year={year}
      districtId={districtId}
      tableCommentedId={`pi4.0-4.1-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "20px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="PI 4.0-4.1 Actual Score" score={score} />
          </IndicatorScoreSection>

          <Title level={5} style={{ marginTop: "20px" }}>
            Evidence of DA support to Public Schools
          </Title>
          <Table
            columns={schoolsSupportColumns}
            dataSource={educationSupport}
            pagination={false} bordered />

          <Title level={5} style={{ marginTop: "20px" }}>
            List of Public Schools
          </Title>
          <Table
            columns={schoolsColumns}
            dataSource={schools}
            pagination={false} bordered />

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion:</Title>
          <Content>
            The Assembly has supported {percentage}% of Public schools to address their challenges in {year}
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default EducationServiceSupport;